import { useQuery } from "@tanstack/react-query";
import { useEntitlements, useSupabase, useTenant } from "@how/app-kit";
import { churchToday, devotionalReflection, type RichText } from "@how/website-core";
import { useTenantRecord } from "../tenant/queries";

/*
 * The member's half of Devotionals (ADR 0077, ADR 0037): the church's devotionals to read, today's first. The policies
 * admit only published devotionals whose day has begun in the church's own time zone, to a member with portal.access in
 * a church with the devotionals module: the database decides what is visible, and nothing here writes.
 */

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export interface PortalDevotional {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  /** The day it is for, YYYY-MM-DD, in the church's calendar. */
  readonly date: string;
  readonly series: { readonly slug: string; readonly title: string } | null;
  readonly scripture: {
    readonly reference: string | null;
    readonly text: string | null;
    readonly version: string | null;
  };
  readonly reflection: RichText | null;
  readonly prayer: string | null;
  readonly author: string | null;
  /** A file in the church's public media area. */
  readonly image: { readonly path: string; readonly alt: string } | null;
}

// author_person_id is staff's business; members see the name the church publishes.
const COLUMNS =
  "id, title, slug, devotional_date, series_title, series_slug, scripture_reference, scripture_text, scripture_version, reflection, prayer, image_path, image_alt, author_name";

interface Row {
  readonly id: string;
  readonly title: string;
  readonly slug: string;
  readonly devotional_date: string;
  readonly series_title: string | null;
  readonly series_slug: string | null;
  readonly scripture_reference: string | null;
  readonly scripture_text: string | null;
  readonly scripture_version: string | null;
  readonly reflection: unknown;
  readonly prayer: string | null;
  readonly image_path: string | null;
  readonly image_alt: string | null;
  readonly author_name: string | null;
}

export function toPortalDevotional(row: Row): PortalDevotional {
  return {
    id: row.id,
    title: row.title,
    slug: row.slug,
    date: row.devotional_date,
    series:
      row.series_title === null || row.series_slug === null
        ? null
        : { slug: row.series_slug, title: row.series_title },
    scripture: {
      reference: row.scripture_reference,
      text: row.scripture_text,
      version: row.scripture_version,
    },
    reflection: devotionalReflection({ reflection: row.reflection }),
    prayer: row.prayer,
    author: row.author_name,
    image: row.image_path === null ? null : { path: row.image_path, alt: row.image_alt ?? "" },
  };
}

/** The church's latest visible devotionals, newest first (the database leaves out drafts and days still to come). */
export function useDevotionals() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["portal-devotionals", tenantId],
    enabled: tenantId !== null,
    queryFn: async (): Promise<PortalDevotional[]> => {
      const { data, error } = await supabase
        .from("devotionals")
        .select(COLUMNS)
        // RLS admits every church the reader belongs to; the screen shows one church.
        .eq("tenant_id", tenantId ?? "")
        .order("devotional_date", { ascending: false })
        .limit(400);
      if (error) throw error;
      return data.map(toPortalDevotional);
    },
  });
}

/** The church's date now: its own time zone once it is known, the reader's own calendar until then. */
export function useChurchToday(): string {
  const record = useTenantRecord();
  return churchToday(
    Date.now(),
    record.data?.time_zone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
}

/** Whether the latest devotional is today's; "the latest" otherwise. */
export function isTodays(devotional: PortalDevotional, today: string): boolean {
  return devotional.date === today;
}

/**
 * Home's devotional: the most recent visible one, only in a church with the module. `data` is null when there is
 * none to show; the card shows nothing rather than an error.
 */
export function useTodaysDevotional() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const entitlements = useEntitlements();
  const hasModule = entitlements.data?.has("devotionals") === true;
  return useQuery({
    queryKey: ["portal-devotional-latest", tenantId],
    enabled: tenantId !== null && hasModule,
    queryFn: async (): Promise<PortalDevotional | null> => {
      const { data, error } = await supabase
        .from("devotionals")
        .select(COLUMNS)
        .eq("tenant_id", tenantId ?? "")
        .order("devotional_date", { ascending: false })
        .limit(1);
      if (error) throw error;
      const first = data[0];
      return first === undefined ? null : toPortalDevotional(first);
    },
  });
}
